'use client'

import { cn } from '@/lib/tailwind'
import { COMPACT_CLASSES } from '@/ui/design-tokens'

interface SuggestedPeopleChipsProps {
  suggestions: string[]
  addingPerson: string | null
  isDisabled: boolean
  onAdd: (name: string) => void
  onDismiss: (name: string) => void
}

/**
 * Suggested people chips from AI analysis.
 * Shows new names detected that aren't in the known people list.
 */
export function SuggestedPeopleChips({
  suggestions,
  addingPerson,
  isDisabled,
  onAdd,
  onDismiss,
}: SuggestedPeopleChipsProps) {
  if (suggestions.length === 0) {
    return null
  }

  return (
    <>
      {suggestions.map((name, idx) => (
        <div
          key={name}
          className={cn(
            'animate-property-enter group inline-flex items-center gap-1',
            addingPerson === name && 'opacity-50',
          )}
          style={{ animationDelay: `${(idx + 1) * 50}ms` }}
        >
          <button
            type="button"
            onClick={() => onAdd(name)}
            disabled={isDisabled || addingPerson === name}
            className={cn(
              COMPACT_CLASSES.base,
              COMPACT_CLASSES.layout,
              'bg-success/10 text-success',
              'hover:bg-success/20',
              'disabled:cursor-not-allowed disabled:opacity-50',
              'transition-all duration-200',
              'max-w-[140px] truncate sm:max-w-[160px]',
            )}
          >
            <span className="truncate">
              {addingPerson === name ? 'Adding...' : `+ ${name}`}
            </span>
          </button>
          {addingPerson !== name && (
            <button
              type="button"
              onClick={() => onDismiss(name)}
              disabled={isDisabled}
              className={cn(
                'flex min-h-[44px] min-w-[44px] items-center justify-center rounded-sm transition-colors sm:h-4 sm:min-h-0 sm:w-4 sm:min-w-0',
                'text-muted-foreground hover:bg-muted/30 hover:text-foreground',
                'disabled:opacity-50',
              )}
              aria-label={`Dismiss suggestion for ${name}`}
            >
              <span className="text-sm">×</span>
            </button>
          )}
        </div>
      ))}
    </>
  )
}
